/**
 * Motor de sincronización.
 *
 * Orden de trabajo, y el orden es importante:
 *
 *  1. **Primero se envía** lo que hay en la cola. Si se trajera antes, el servidor
 *     aún no sabría lo que hicimos sin conexión y podríamos pisar nuestros propios
 *     cambios con la copia vieja.
 *  2. **Después se trae** todo lo que cambió desde el cursor.
 *  3. **Se guarda el cursor** que devuelve el servidor, no la hora del dispositivo:
 *     el reloj de un teléfono puede ir mal, y entonces se perderían cambios.
 *
 * Se sincroniza al arrancar, al volver la conexión y cada cierto tiempo. Si algo
 * falla, no se borra nada de la cola: se anota el intento y se reintenta después.
 */

import { STORES, putAll, setMeta, getAll } from './db.js';
import {
  clearApplied,
  markFailed,
  pendingOperations,
  readCursor,
  writeCursor,
  writeLastSync,
  type QueuedOperation,
} from './outbox.js';
import { syncPull, syncPush } from '../endpoints.js';
import type { SyncOperationResult } from '@eldescuadre/contracts';

/** Cuántas operaciones se envían en cada lote. */
const BATCH_SIZE = 50;

/** Resultado de una pasada de sincronización. */
export interface SyncOutcome {
  pushed: number;
  failed: number;
  pulled: number;
  /** Operaciones que el servidor rechazó, con su motivo. */
  rejected: Array<{ id: string; error: string }>;
  /** Si la sincronización no se pudo completar. */
  error?: string | undefined;
}

/**
 * Envía la cola al servidor y se lleva los cambios.
 *
 * Nunca lanza: devuelve el resultado. Una sincronización que falla no debe romper
 * la pantalla que el usuario está usando.
 */
export async function synchronize(): Promise<SyncOutcome> {
  const outcome: SyncOutcome = { pushed: 0, failed: 0, pulled: 0, rejected: [] };

  try {
    // 1. Enviar lo pendiente, por lotes y en orden.
    const pending = await pendingOperations();

    for (let index = 0; index < pending.length; index += BATCH_SIZE) {
      const batch = pending.slice(index, index + BATCH_SIZE);
      const result = await syncPush(batch);
      const byId = new Map<string, SyncOperationResult>(
        result.results.map((item) => [item.id, item]),
      );

      const applied: string[] = [];

      for (const operation of batch) {
        const item = byId.get(operation.id);

        if (item?.ok) {
          applied.push(operation.id);
          outcome.pushed += 1;
        } else if (item) {
          // El servidor lo rechazó con un motivo: se queda en la cola, anotado,
          // para que el usuario pueda verlo. Reintentarlo sin más no arreglaría
          // nada, así que no se descarta en silencio.
          outcome.failed += 1;
          outcome.rejected.push({ id: operation.id, error: item.error ?? 'Rechazado.' });
          await markFailed(operation.id, item.error ?? 'Rechazado por el servidor.');
        }
      }

      await clearApplied(applied);
    }

    // 2. Traer lo que cambió.
    const cursor = await readCursor();
    const pulled = await syncPull(cursor ?? undefined);

    await applyPull(pulled);
    outcome.pulled =
      pulled.transactions.length +
      pulled.accounts.length +
      pulled.categories.length +
      pulled.budgets.length +
      pulled.debts.length +
      pulled.fiadoStores.length +
      pulled.recurringRules.length +
      pulled.splits.length +
      pulled.goals.length;

    // 3. Guardar el cursor del servidor, nunca el reloj del dispositivo.
    await writeCursor(pulled.cursor);
    await writeLastSync(new Date().toISOString());
    await setMeta('serverToday', pulled.today);

    return outcome;
  } catch (error) {
    outcome.error =
      error instanceof Error ? error.message : 'No se pudo sincronizar. Se reintentará.';
    return outcome;
  }
}

/**
 * Guarda en el almacén local lo que llegó del servidor.
 *
 * Se guarda **todo**, incluidos los borrados: los que tienen fecha de borrado se
 * quitan de la copia local, que es como el cliente se entera de que ya no están.
 */
async function applyPull(pulled: Awaited<ReturnType<typeof syncPull>>): Promise<void> {
  // Las colecciones sin identificador propio se guardan igual: todas lo tienen.
  const collections: Array<[keyof typeof STORES, ReadonlyArray<unknown>]> = [
    ['accounts', pulled.accounts],
    ['categories', pulled.categories],
    ['transactions', pulled.transactions],
    ['transactionItems', pulled.transactionItems],
    ['budgets', pulled.budgets],
    ['debts', pulled.debts],
    ['debtPayments', pulled.debtPayments],
    ['fiadoStores', pulled.fiadoStores],
    ['fiadoEntries', pulled.fiadoEntries],
    ['recurringRules', pulled.recurringRules],
    ['splits', pulled.splits],
    ['goals', pulled.goals],
    ['goalContributions', pulled.goalContributions],
    ['tags', pulled.tags],
  ];

  for (const [store, rows] of collections) {
    const records = (rows as Array<{ id: string; deletedAt?: string | null }>).filter(
      (row) => row && typeof row.id === 'string',
    );

    const alive = records.filter((row) => !row.deletedAt);
    const gone = records.filter((row) => row.deletedAt);

    if (alive.length > 0) {
      await putAll(STORES[store], alive as Array<{ id: string }>);
    }

    // Un registro borrado en el servidor se quita de la copia local.
    if (gone.length > 0) {
      const { removeMany } = await import('./db.js');
      await removeMany(
        STORES[store],
        gone.map((row) => row.id),
      );
    }
  }
}

/**
 * Lee los movimientos de la copia local, para poder pintarlos sin conexión.
 * Se ordenan del más reciente al más antiguo, como en la pantalla.
 */
export async function localTransactions(): Promise<
  Array<{
    id: string;
    date: string;
    time: string;
    kind: string;
    amount: number;
    merchant: string | null;
    categoryId: string | null;
    accountId: string | null;
    deletedAt?: string | null;
  }>
> {
  const rows = await getAll<{
    id: string;
    date: string;
    time: string;
    kind: string;
    amount: number;
    merchant: string | null;
    categoryId: string | null;
    accountId: string | null;
    deletedAt?: string | null;
  }>(STORES.transactions);

  return rows
    .filter((row) => !row.deletedAt)
    .sort((a, b) => `${b.date}${b.time}`.localeCompare(`${a.date}${a.time}`));
}

/** Vacía la copia local y la cola. Se llama al cerrar sesión. */
export async function resetLocalData(): Promise<void> {
  const { wipeEverything } = await import('./db.js');
  await wipeEverything();
}

/** Operaciones que quedaron rechazadas, para poder avisar al usuario. */
export async function rejectedOperations(): Promise<QueuedOperation[]> {
  return (await pendingOperations()).filter((operation) => operation.attempts > 0);
}
